import { motion, AnimatePresence } from 'framer-motion'
import { useBlackjackGame } from './hooks/useBlackjackGame'
import { BlackjackTable } from './components/BlackjackTable'
import { ActionButtons } from './components/ActionButtons'
import { ChipSelector, Chip } from '@/components/common/Chip'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/features/auth/contexts/AuthContext'
import { cn } from '@/lib/utils'

const resultMessages: Record<string, { text: string; color: string }> = {
  blackjack: { text: '¡BLACKJACK!', color: 'text-casino-gold' },
  win: { text: '¡GANASTE!', color: 'text-green-500' },
  lose: { text: 'PERDISTE', color: 'text-red-500' },
  push: { text: 'EMPATE', color: 'text-blue-400' },
  bust: { text: 'TE PASASTE', color: 'text-red-500' },
}

export function Blackjack() {
  const { user } = useAuth()
  const {
    playerHand,
    dealerHand,
    betAmount,
    selectedChip,
    gamePhase,
    result,
    lastWinnings,
    isDealing,
    showDealerCard,
    dealingTo,
    playerValue,
    dealerValue,
    canDouble,
    canSplit,
    addBet,
    selectChip,
    clearBet,
    deal,
    hit,
    stand,
    double,
    newGame,
  } = useBlackjackGame()

  const isBetting = gamePhase === 'betting'
  const isPlaying = gamePhase === 'playerTurn'
  const isResult = gamePhase === 'result'

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-casino-black to-casino-black-light">
      {/* Table */}
      <BlackjackTable
        playerHand={playerHand}
        dealerHand={dealerHand}
        playerValue={playerValue}
        dealerValue={dealerValue}
        showDealerCard={showDealerCard}
        result={result}
        dealingTo={dealingTo}
      />

      {/* Controls area */}
      <div className="bg-casino-black/90 border-t border-casino-gold/30 p-4">
        {/* Betting phase */}
        {isBetting && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {/* Chip selector */}
            <ChipSelector
              selectedValue={selectedChip}
              onSelect={selectChip}
              disabled={isDealing}
            />

            {/* Current bet display */}
            <div className="flex items-center justify-center gap-4">
              <span className="text-casino-gold/60 font-cinzel">APUESTA:</span>
              <motion.span
                key={betAmount}
                initial={{ scale: 1.2 }}
                animate={{ scale: 1 }}
                className="text-2xl font-bold text-casino-gold"
              >
                ${betAmount.toLocaleString()}
              </motion.span>
              {betAmount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearBet}
                  className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                >
                  Limpiar
                </Button>
              )}
            </div>

            {/* Bet area - click to add chips */}
            <div className="flex justify-center">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => addBet(selectedChip)}
                disabled={isDealing || !user || (user.balance < selectedChip)}
                className={cn(
                  'w-48 h-24 rounded-xl border-2 border-dashed',
                  'flex flex-col items-center justify-center gap-2',
                  'transition-colors duration-200',
                  betAmount > 0
                    ? 'border-casino-gold bg-casino-gold/10'
                    : 'border-casino-gold/40 bg-casino-black/50 hover:border-casino-gold/60'
                )}
              >
                {betAmount > 0 ? (
                  <div className="flex gap-1">
                    {[...Array(Math.min(5, Math.ceil(betAmount / 1000)))].map((_, i) => (
                      <Chip key={i} value={selectedChip} size="sm" />
                    ))}
                  </div>
                ) : (
                  <span className="text-casino-gold/60 font-cinzel text-sm">
                    Click para apostar
                  </span>
                )}
              </motion.button>
            </div>

            {/* Deal button */}
            <div className="flex justify-center">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  onClick={deal}
                  disabled={betAmount <= 0 || isDealing}
                  className={cn(
                    'px-12 py-4 font-cinzel text-xl',
                    'bg-gradient-to-b from-casino-gold to-casino-gold-dark',
                    'hover:from-casino-gold-light hover:to-casino-gold',
                    'text-casino-black border-2 border-casino-gold-light',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  REPARTIR
                </Button>
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* Playing phase */}
        {isPlaying && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {/* Current bet */}
            <div className="text-center">
              <span className="text-casino-gold/60 font-cinzel">APUESTA: </span>
              <span className="text-xl font-bold text-casino-gold">
                ${betAmount.toLocaleString()}
              </span>
            </div>

            {/* Action buttons */}
            <ActionButtons
              onHit={hit}
              onStand={stand}
              onDouble={double}
              canDouble={canDouble}
              canSplit={canSplit}
              disabled={isDealing}
            />
          </motion.div>
        )}

        {/* Dealer turn phase */}
        {gamePhase === 'dealerTurn' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-4"
          >
            <span className="text-casino-gold font-cinzel text-lg animate-pulse">
              Turno del dealer...
            </span>
          </motion.div>
        )}

        {/* Dealing phase */}
        {gamePhase === 'dealing' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-4"
          >
            <span className="text-casino-gold font-cinzel text-lg animate-pulse">
              Repartiendo cartas...
            </span>
          </motion.div>
        )}
      </div>

      {/* Result overlay */}
      <AnimatePresence>
        {isResult && result && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
            onClick={newGame}
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              transition={{ type: 'spring', damping: 15 }}
              className="text-center p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <motion.h2
                initial={{ y: -20 }}
                animate={{ y: 0 }}
                className={cn(
                  'font-cinzel text-5xl font-bold mb-4',
                  resultMessages[result].color
                )}
              >
                {resultMessages[result].text}
              </motion.h2>

              {lastWinnings > 0 && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2 }}
                  className="mb-6"
                >
                  <span className="text-casino-gold/60 font-cinzel">GANANCIA: </span>
                  <span className="text-3xl font-bold text-casino-gold">
                    ${lastWinnings.toLocaleString()}
                  </span>
                </motion.div>
              )}

              {result === 'push' && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2 }}
                  className="mb-6"
                >
                  <span className="text-blue-400 font-cinzel">
                    Apuesta devuelta: ${betAmount.toLocaleString()}
                  </span>
                </motion.div>
              )}

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <Button
                  onClick={newGame}
                  className={cn(
                    'px-12 py-4 font-cinzel text-xl',
                    'bg-gradient-to-b from-casino-gold to-casino-gold-dark',
                    'hover:from-casino-gold-light hover:to-casino-gold',
                    'text-casino-black border-2 border-casino-gold-light'
                  )}
                >
                  NUEVA PARTIDA
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
